import moment from 'moment'
import { DEPLOY_ENVIRONMENT, SITE_TYPE, ConfigManager } from '~/managers/ConfigManager'
import { BANK_TRANSFER_NETWORK, BlockchainNetwork, CURRENCY } from '~/types'

export const SESSION_STORAGE_VALUES = {
  hasNewAddressModalAcknowledged: 'newAddressModalAcknowledged',
  dismissXusdTooltip: 'dismissXusdTooltip',
  activationChecklistShown: 'activationChecklistShownThisSession',
} as const

export const LOCAL_STORAGE_VALUES = {
  myInfoState: 'myInfoState',
} as const

export const ETH_WITHDRAW_FEES_CAP = {
  biz: {
    fee: 30,
  },
  personal: {
    fee: 30,
  },
} as const

export const POLYGON_WITHDRAW_FEES_CAP = {
  personal: {
    fee: 1,
  },
  biz: {
    fee: 5,
  },
} as const

export const AVALANCHE_WITHDRAW_FEES_CAP = {
  personal: {
    fee: 1,
  },
  biz: {
    fee: 1,
  },
} as const

export const BNBSMART_WITHDRAW_FEES_CAP = {
  personal: {
    fee: 1,
  },
  biz: {
    fee: 1,
  },
} as const

export const TRON_FIXED_WITHDRAW_FEES_CAP = {
  personal: {
    fee: 2,
  },
  biz: {
    fee: 2,
  },
} as const

export const TRON_WITHDRAW_FEES_CAP = {
  personal: {
    fee: 30,
  },
  biz: {
    fee: 30,
  },
} as const

export const coolingDownThresholdInMinutes = 720

export const SUPPORT_LINKS = {
  SUPPORT_HUB: 'https://www.straitsx.com/support',
  HOME: 'https://support.straitsx.com/support/home',
  NEW_REQUEST: 'https://support.straitsx.com/support/tickets/new',
  NEW_REQUEST_PERSONAL:
    'https://support.straitsx.com/support/tickets/new?ticket_form=personal_account',
  SINGAPORE_BANKS:
    'https://support.straitsx.com/support/solutions/articles/157000365662-supported-bank-accounts-for-sgd-and-usd-transfers',
  KILLSWITCH_SUPPORT:
    'https://support.straitsx.com/support/solutions/articles/157000365559-kill-switch',
  TRANSFER_IN_STABLECOIN:
    'https://support.straitsx.com/support/solutions/articles/157000365758-transfer-in-xsgd-and-xusd-stablecoins',
  TRANSFERRING_XSGD:
    'https://support.straitsx.com/support/solutions/articles/157000365572-transferring-xsgd-in',
  TRANSFER_IN_FIAT:
    'https://support.straitsx.com/support/solutions/articles/157000365766-transfer-in-fiat-sgd-and-usd',
  LEARN_MORE_XSGD: 'https://www.straitsx.com/xsgd',
  LEARN_MORE_XUSD: 'https://www.straitsx.com/xusd',
  LEARN_MORE_INTERMEDIARY_SWIFT_CODE:
    'https://support.straitsx.com/support/solutions/articles/157000365575-adding-a-routing-code-or-intermediary-swift-bic-code-for-international-usd-withdrawals',
  LEARN_MORE_ROUTING_CODE:
    'https://support.straitsx.com/support/solutions/articles/157000365575-adding-a-routing-code-or-intermediary-swift-bic-code-for-international-usd-withdrawals',
  TRANSFER_OUT_STABLECOIN:
    'https://support.straitsx.com/support/solutions/articles/157000365770-transfer-out-xsgd-and-xusd-stablecoins',
  TRANSFER_OUT_XSGD:
    'https://support.straitsx.com/support/solutions/articles/157000365581-transferring-xsgd-out',
  TRANSFER_OUT_FIAT:
    'https://support.straitsx.com/support/solutions/articles/157000365779-transfer-out-fiat-sgd-and-usd',
  TRANSFERRING_USDT:
    'https://support.straitsx.com/support/solutions/articles/157000365588-transferring-usdt-in',
  TRANSFERRING_USDC:
    'https://support.straitsx.com/support/solutions/articles/157000365593-transferring-usdc-in',
  WHITELISTING_BANK_ACCOUNT:
    'https://support.straitsx.com/support/solutions/articles/157000365826-sgd-and-usd-bank-account-verification',
  WHITELISTING_REQUEST:
    'https://support.straitsx.com/support/solutions/articles/157000363445-how-do-i-submit-a-whitelisting-request-for-my-personal-account-',
  SETUP_2FA_LINK:
    'https://support.straitsx.com/support/solutions/articles/157000365916-how-to-set-up-or-update-two-factor-authentication-2fa-',
  UPDATE_2FA_DEVICE:
    'https://support.straitsx.com/support/solutions/articles/157000365615-how-to-update-2-factor-authentication-2fa-device-or-app',
  WHAT_IS_XSGD:
    'https://support.straitsx.com/support/solutions/articles/157000363433-what-is-xsgd-',
  // TODO(jewel-chin): update this xidr broken link
  WHAT_IS_XIDR: 'https://straitsx-ops.zendesk.com/hc/en-us/articles/40297111549721-What-is-XIDR',
  TERMS_OF_SERVICE: 'https://www.straitsx.com/sg/terms-of-service-new',
  STRAITSX_WHAT_CAN_I_DO:
    'https://support.straitsx.com/support/solutions/articles/157000365625-what-can-i-do-with-my-straitsx-personal-account-',
  HOW_TO_VERIFY_MY_ACCOUNT:
    'https://support.straitsx.com/support/solutions/articles/157000365656-address-verification-with-browser-wallets',
  DIFFERENCE_CUSTODIAL_NON_CUSTODIAL:
    'https://support.straitsx.com/support/solutions/articles/157000365658-what-is-the-difference-between-custodial-and-non-custodial-private-addresses-',
  VERIFY_INTERNATIONAL_BASED_ACCOUNT:
    'https://support.straitsx.com/support/solutions/articles/157000365659-how-to-verify-an-international-based-account',
  WHAT_IS_RATE:
    'https://support.straitsx.com/support/solutions/articles/157000365660-what-are-the-rates-and-how-is-it-paid-out-',
  HOW_MUCH_CAN_I_TRANSFER_EARN:
    'https://support.straitsx.com/support/solutions/articles/157000365661-transaction-limit-for-straitsx-earn',
  EARN: 'https://support.straitsx.com/support/solutions/folders/157000939309',
  HOW_MANY_VERIFIED_BANK_ACCOUNT:
    'https://support.straitsx.com/support/solutions/articles/157000365663-how-many-verified-bank-accounts-can-i-have-',
  SUBMIT_WHITELIST_REQUEST:
    'https://support.straitsx.com/support/solutions/articles/157000363445-how-do-i-submit-a-whitelisting-request-for-my-personal-account-',
  ADD_XSGD:
    'https://support.straitsx.com/support/solutions/articles/157000365664-how-do-i-add-the-xsgd-token-to-my-eth-polygon-avalanche-arbitrum-zilliqa-xrp-ledger-or-hedera-wallet-',
  PERSONAL_TRANSACTION_FEE:
    'https://support.straitsx.com/support/solutions/articles/157000365665-straitsx-personal-account-transaction-fee-schedule',
  MINT_FEE_AND_LIMITS:
    'https://support.straitsx.com/support/solutions/articles/157000365666-transaction-fees-and-limits-for-straitsx-mint',
  WHAT_IS_MINT:
    'https://support.straitsx.com/support/solutions/articles/157000365667-what-is-mint-',
  INTERNAL_RISK_HELP:
    'https://support.straitsx.com/support/solutions/articles/157000365668-why-am-i-getting-an-internal-risk-procedure-error-when-i-try-to-do-a-transaction-',
  RIPPLE_FUNGIBLE_TOKEN:
    'https://support.straitsx.com/hc/en-us/articles/47270191571225-Enabling-the-XSGD-Token-on-XRP-Ledger',

  // TODO (jewel-chin): update broken links
  WHAT_IS_SANDBOX_ACCOUNT:
    'https://support.straitsx.com/support/solutions/articles/157000366378-what-is-a-sandbox-account-',
  WHITELIST_ADDRESS_LINK:
    'https://support.straitsx.com/hc/en-us/articles/4410434204185-How-do-I-submit-a-whitelisting-request-',
  PRIVACY_SECURITY:
    'https://support.straitsx.com/hc/en-us/categories/4406900894489-Privacy-Security',
  BLOCKCHAIN_TRANSFER_OUT_FEE:
    'https://support.straitsx.com/hc/en-us/articles/4410447472409-StraitsX-Business-Account-Transaction-Fee-Schedule',
  XSGD_HOLDING_LIMIT:
    'https://support.straitsx.com/hc/en-us/articles/4410453314713-What-is-XSGD-holding-limit-',
  HOLDING_LIMIT_BALANCE: 'https://support.straitsx.com/hc/en-us/articles/4407435991321',
  VERIFY_LOCAL_BASED_ACCOUNT:
    'https://support.straitsx.com/hc/en-us/articles/4407118693785-How-to-verify-my-account-',
} as const

export const SUPPORT_LINKS_ID = {
  HOME: 'https://support.id.straitsx.com/support/solutions',
  NEW_REQUEST: 'https://support.id.straitsx.com/support/requests/new',
  ACCOUNT_HOW_TO_VERIFY:
    'https://support.id.straitsx.com/support/solutions/articles/157000365920-how-do-i-verify-my-account-',
  BANK_ACCOUNT_HOW_TO_VERIFY:
    'https://support.id.straitsx.com/support/solutions/articles/157000365926-how-do-i-verify-my-bank-account-',
  BIZ_ACCOUNT_HOW_TO_VERIFY:
    'https://support.straitsx.com/support/solutions/articles/157000365896-how-to-verify-my-indonesian-business-account-',
  BLOCKCHAIN_ADDRESS_TYPES:
    'https://support.id.straitsx.com/support/solutions/articles/157000365927-which-one-to-choose-custodial-or-non-custodial-address-',
  BLOCKCHAIN_HOW_TO_WHITELIST_ADDRESS:
    'https://support.id.straitsx.com/support/solutions/articles/157000365947-how-do-i-submit-a-whitelisting-request-for-my-personal-address-',
  NETWORK_FEE_LEARN_MORE: 'https://support.id.straitsx.com/support/solutions',
  IDR_TRANSFER_OUT:
    'https://support.id.straitsx.com/support/solutions/articles/157000365948-transfer-out-indonesian-rupiah',
  XIDR_TRANSFER_IN:
    'https://support.id.straitsx.com/support/solutions/articles/157000365949-transferring-xidr-in',
  XIDR_ADD_TO_WALLET:
    'https://support.id.straitsx.com/hc/en-us/articles/40296867098265-How-do-I-add-the-XIDR-token-to-my-ETH-or-ZIL-wallet',
} as const

export const usdBasedCurrency = [CURRENCY.USDC, CURRENCY.XUSD, CURRENCY.USDT, CURRENCY.USD]

export const BLOCKCHAIN_NETWORKS = {
  ETHEREUM: 'ethereum',
  POLYGON: 'polygon',
  AVALANCHE: 'avalanche',
  SOLANA: 'solana',
  ARBITRUM: 'arbitrum',
  HEDERA: 'hedera',
  BNBSMART: 'bnbsmart',
  TRON: 'tron',
  RIPPLE: 'ripple',
  ZILLIQA: 'zilliqa',
} as const

export const BLOCKCHAIN_TOKENS = {
  USDC_ERC20: 'USDC_ERC20',
  USDC_MATIC: 'USDC_MATIC',
  USDT_BEP20: 'USDT_BEP20',
  USDT_ERC20: 'USDT_ERC20',
  USDT_TRC20: 'USDT_TRC20',
  XSGD_ARB: 'XSGD_ARB',
  XSGD_AVAX: 'XSGD_AVAX',
  XSGD_ERC20: 'XSGD_ERC20',
  XSGD_HTS: 'XSGD_HTS',
  XSGD_MATIC: 'XSGD_MATIC',
  XSGD_XRP: 'XSGD_XRP',
  XSGD_ZRC2: 'XSGD_ZRC2',
  XSGD_SPL: 'XSGD_SPL',
  XUSD_BEP20: 'XUSD_BEP20',
  XUSD_ERC20: 'XUSD_ERC20',
  XUSD_SPL: 'XUSD_SPL',
} as const

export const BANK_TOKENS = {
  SGD_FAST: 'SGD_FAST',
  SGD_MEPS: 'SGD_MEPS',
  SGD_SWIFT: 'SGD_SWIFT',
  USD_SWIFT: 'USD_SWIFT',
  XSGD_FAST: 'XSGD_FAST',
  XSGD_MEPS: 'XSGD_MEPS',
  XSGD_SWIFT: 'XSGD_SWIFT',
  XUSD_SWIFT: 'XUSD_SWIFT',
} as const

export const BLOCKCHAIN_NETWORK_ARRAY = Object.values(BLOCKCHAIN_NETWORKS)
export const EVM_COMPATIBLE_NETWORK = 'evm-compatible-network'
export const EVM_COMPATIBLE_BLOCKCHAIN_NETWORKS: BlockchainNetwork[] = [
  'ethereum',
  'polygon',
  'avalanche',
  'arbitrum',
  'bnbsmart',
]

export const BLOCKCHAIN_NETWORKS_WITH_FEES: BlockchainNetwork[] = [
  ...EVM_COMPATIBLE_BLOCKCHAIN_NETWORKS,
  'zilliqa',
  'tron',
  'ripple',
]

export const BLOCKCHAIN_NETWORKS_WITHOUT_FEES_ESTIMATION: BlockchainNetwork[] = ['tron']

export const posthogConfig = {
  key: 'phc_9LJpFClEPPQxTDpLSVJaVOZJknV4IDHxpzpvGEiYNLz',
  apiHost: 'https://us.i.posthog.com',
}

export const USDC_POLYGON_CONTRACT_ADDRESS = '0x3c499c542cef5e3811e1192ce70d8cc03d5c3359'

export const XSGD_ARBITRUM_CONTRACT_ADDRESS = '0xE333e7754a2DC1E020a162Ecab019254b9DaB653'

const SSO_HOST =
  ConfigManager.getEnvironmentMode() === DEPLOY_ENVIRONMENT.PRODUCTION
    ? 'https://sso.straitsx.com/auth'
    : 'https://sso.dev.straitsx.com/auth'

const CLIENT_ID = ConfigManager.getSiteType() === SITE_TYPE.APP ? 'xfers-app' : 'xfers-biz'

export const SSO_2FA_REMOVAL_URL = `${SSO_HOST}/realms/xfers-sg/login-actions/reset-credentials?client_id=${CLIENT_ID}&reset_cred_type=remove_totp`

export const APPROVAL_REQUEST_TYPE_APPROVE = 'approve'
export const APPROVAL_REQUEST_TYPE_REJECT = 'reject'

export const minCsvExportStartDate = moment('2023-01-01', 'YYYY-MM-DD')

export const ddRumEvents = {
  missingAssetSelection: 'missing_asset_selection',
  missingWithdrawalButton: 'missing_withdrawal_button',
  disabledWithdrawalButton: 'disabled_withdrawal_button',
  missingNetworkSelection: 'missing_network_selection',
} as const

export const ddRumFlows = {
  fiatWithdrawal: 'fiat_withdrawal',
}

export const CURRENCY_WITH_NETWORK_CONTRACT_ADDRESS_LIST = {
  [CURRENCY.XSGD]: {
    [BLOCKCHAIN_NETWORKS.ETHEREUM]: '0x70e8de73ce538da2beed35d14187f6959a8eca96',
    [BLOCKCHAIN_NETWORKS.POLYGON]: '0xDC3326e71D45186F113a2F448984CA0e8D201995',
    [BLOCKCHAIN_NETWORKS.AVALANCHE]: '0xb2F85b7AB3c2b6f62DF06dE6aE7D09c010a5096E',
    [BLOCKCHAIN_NETWORKS.ARBITRUM]: '0xE333e7754a2DC1E020a162Ecab019254b9DaB653',
    [BLOCKCHAIN_NETWORKS.RIPPLE]: 'rK67JczCpaYXVtfw3qJVmqwpSfa1bYTptw',
    [BLOCKCHAIN_NETWORKS.ZILLIQA]: 'zil1zu72vac254htqpg3mtywdcfm84l3dfd9qzww8t',
    [BLOCKCHAIN_NETWORKS.HEDERA]: '0.0.1985922',
    [BLOCKCHAIN_NETWORKS.SOLANA]: '71S9cppWipeUEQDFngYwxjoxB6Sz1MUqX72byLsVYJqy',
  },
  [CURRENCY.XUSD]: {
    [BLOCKCHAIN_NETWORKS.ETHEREUM]: '0xC08e7E23C235073C6807C2EFE7021304cb7c2815',
    [BLOCKCHAIN_NETWORKS.BNBSMART]: '0xF81aC2E1A0373ddE1BCE01E2Fe694a9b7E3bfcB9',
    [BLOCKCHAIN_NETWORKS.SOLANA]: '4UbvZiomFvXDnZSz6vdHiDNiHozH2ykTEqjhhbVHiv9z',
  },
  [CURRENCY.USDC]: {
    [BLOCKCHAIN_NETWORKS.ETHEREUM]: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    [BLOCKCHAIN_NETWORKS.POLYGON]: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
  },
  [CURRENCY.USDT]: {
    [BLOCKCHAIN_NETWORKS.ETHEREUM]: '0xdac17f958d2ee523a2206206994597c13d831ec7',
    [BLOCKCHAIN_NETWORKS.TRON]: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
    [BLOCKCHAIN_NETWORKS.BNBSMART]: '0x55d398326f99059fF775485246999027B3197955',
  },
} as const

export const NETWORK_TO_TOKEN_STANDARD = {
  [BLOCKCHAIN_NETWORKS.POLYGON]: 'Polygon',
  [BLOCKCHAIN_NETWORKS.ARBITRUM]: 'Arbitrum',
  [BLOCKCHAIN_NETWORKS.AVALANCHE]: 'Avalanche C-Chain',
  [BLOCKCHAIN_NETWORKS.BNBSMART]: 'BEP-20',
  [BLOCKCHAIN_NETWORKS.TRON]: 'TRC-20',
  [BLOCKCHAIN_NETWORKS.ZILLIQA]: 'ZRC-2',
  [BLOCKCHAIN_NETWORKS.HEDERA]: 'Hedera',
  [BLOCKCHAIN_NETWORKS.RIPPLE]: 'XRP Ledger',
  [BLOCKCHAIN_NETWORKS.ETHEREUM]: 'ERC-20',
} as const

export const TRANSFER_REMARKS_CHAR_LIMIT = {
  [BANK_TRANSFER_NETWORK.FAST]: 35,
  [BANK_TRANSFER_NETWORK.MEPS]: 105,
  [BANK_TRANSFER_NETWORK.SWIFT]: 105,
}
